import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Shield, Zap, Package, Truck, CheckCircle, ArrowRight, Star, Award, Users, Settings, Droplets, Timer, Target, Coffee, Apple, Beef, Wheat, Phone, Mail, Download, Eye, BarChart3, Wifi, Play, ChevronDown, Menu, X, Layers, Monitor, Smartphone } from 'lucide-react';

const LaserPrinterWebsite = () => {
  const [selectedPrinter, setSelectedPrinter] = useState(0);
  const [activeFeature, setActiveFeature] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY;
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      setScrollProgress((scrolled / maxScroll) * 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const printers = [
    {
      name: 'DateMaster Compact',
      model: 'DM-100',
      type: 'Desktop Solution',
      price: 'From $1,899',
      description: 'Streamlined printing for small to medium food operations. Perfect for local bakeries, cafes, and specialty food producers.',
      image: '🖨️',
      color: '#0071bc',
      lightColor: '#e6f3ff',
      features: [
        'Touch-screen control panel',
        'Food-grade ink system',
        'Wireless connectivity',
        'Automatic date sequencing'
      ],
      stats: [
        { label: 'Speed', value: '120/min' },
        { label: 'Resolution', value: '300 DPI' },
        { label: 'Setup', value: '5 min' }
      ]
    },
    {
      name: 'ProLine Industrial',
      model: 'PL-500',
      type: 'High-Volume System',
      price: 'From $6,999',
      description: 'Robust industrial solution built for continuous operation in demanding production environments.',
      image: '⚙️',
      color: '#005a94',
      lightColor: '#e1ecf7',
      features: [
        'Stainless steel construction',
        'Multi-head configuration',
        'Remote diagnostics',
        'Predictive maintenance'
      ],
      stats: [
        { label: 'Speed', value: '500/min' },
        { label: 'Resolution', value: '600 DPI' },
        { label: 'Uptime', value: '99.8%' }
      ]
    },
    {
      name: 'SmartCode AI',
      model: 'SC-1000',
      type: 'AI-Powered Platform',
      price: 'From $12,499',
      description: 'Next-generation intelligent printing with machine learning optimization and predictive analytics.',
      image: '🤖',
      color: '#1e40af',
      lightColor: '#dbeafe',
      features: [
        'AI optimization engine',
        'Cloud analytics platform',
        'Voice command interface',
        'Blockchain integration'
      ],
      stats: [
        { label: 'Speed', value: '1000/min' },
        { label: 'Resolution', value: '1200 DPI' },
        { label: 'Accuracy', value: '99.99%' }
      ]
    }
  ];

  const industries = [
    { name: 'Beverages', icon: Coffee, count: '200+', color: '#f59e0b' },
    { name: 'Bakery', icon: Wheat, count: '150+', color: '#d97706' },
    { name: 'Dairy', icon: Droplets, count: '100+', color: '#0284c7' },
    { name: 'Meat', icon: Beef, count: '80+', color: '#dc2626' },
  ];

  return (
    <div className="bg-white overflow-hidden">
      {/* Progress Bar */}
      <div className="fixed top-0 left-0 w-full h-1 bg-gray-200 z-50">
        <div 
          className="h-full bg-gradient-to-r from-[#0071bc] to-[#1e40af] transition-all duration-150"
          style={{ width: `${scrollProgress}%` }}
        ></div>
      </div>

      {/* Hero Section - Split Design */}
      <section className="relative min-h-screen flex items-center">
        <div className="w-full max-w-7xl mx-auto grid lg:grid-cols-2 gap-0 items-center">
          {/* Left Content */}
          <div className="px-8 lg:px-16 py-20">
            <div className="space-y-8">
              {/* Badge */}
              <div className="inline-flex items-center bg-[#0071bc]/10 border border-[#0071bc]/20 rounded-full px-6 py-2">
                <div className="w-2 h-2 bg-[#0071bc] rounded-full mr-3 animate-pulse"></div>
                <span className="text-[#0071bc] font-semibold text-sm">FDA CERTIFIED SOLUTIONS</span>
              </div>

              {/* Main Title */}
              <div className="space-y-4">
                <h1 className="text-6xl lg:text-7xl font-black leading-none">
                  <span className="block text-gray-900">SMART</span>
                  <span className="block text-[#0071bc]">FOOD</span>
                  <span className="block text-gray-700 text-4xl font-light">printing systems</span>
                </h1>
              </div>

              {/* Description */}
              <p className="text-xl text-gray-600 leading-relaxed max-w-lg">
                Revolutionary date coding and batch printing solutions engineered for the modern food industry.
              </p>

              {/* Stats Row */}
              <div className="flex items-center gap-8 pt-4">
                <div className="text-center">
                  <div className="text-3xl font-bold text-[#0071bc]">500+</div>
                  <div className="text-sm text-gray-500">Active Users</div>
                </div>
                <div className="w-px h-12 bg-gray-200"></div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-[#0071bc]">99.9%</div>
                  <div className="text-sm text-gray-500">Reliability</div>
                </div>
                <div className="w-px h-12 bg-gray-200"></div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-[#0071bc]">24/7</div>
                  <div className="text-sm text-gray-500">Support</div>
                </div>
              </div>

              {/* CTA */}
              <div className="flex items-center gap-4 pt-8">
                <button className="bg-[#0071bc] hover:bg-[#005a94] text-white px-8 py-4 rounded-2xl font-bold transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl">
                  EXPLORE SOLUTIONS
                </button>
                <button className="flex items-center gap-2 text-[#0071bc] font-semibold hover:gap-3 transition-all duration-300">
                  <Play className="w-5 h-5" />
                  Watch Demo
                </button>
              </div>
            </div>
          </div>

          {/* Right Visual */}
          <div className="relative h-screen lg:h-full">
            <div className="absolute inset-0 bg-gradient-to-br from-[#0071bc]/5 to-[#1e40af]/10"></div>
            
            {/* Floating Cards */}
            <div className="relative h-full flex items-center justify-center p-8">
              <div className="grid grid-cols-2 gap-6 max-w-md">
                {/* Card 1 */}
                <div className="bg-white rounded-3xl p-6 shadow-2xl transform rotate-3 hover:rotate-0 transition-all duration-500 border border-gray-100">
                  <div className="text-4xl mb-4">🖨️</div>
                  <div className="text-sm font-bold text-gray-900 mb-2">DateMaster Compact</div>
                  <div className="text-xs text-gray-500">120 prints/min</div>
                </div>

                {/* Card 2 */}
                <div className="bg-white rounded-3xl p-6 shadow-2xl transform -rotate-2 hover:rotate-0 transition-all duration-500 border border-gray-100 mt-12">
                  <div className="text-4xl mb-4">⚙️</div>
                  <div className="text-sm font-bold text-gray-900 mb-2">ProLine Industrial</div>
                  <div className="text-xs text-gray-500">500 prints/min</div>
                </div>

                {/* Card 3 */}
                <div className="bg-white rounded-3xl p-6 shadow-2xl transform rotate-1 hover:rotate-0 transition-all duration-500 border border-gray-100 -mt-8">
                  <div className="text-4xl mb-4">📱</div>
                  <div className="text-sm font-bold text-gray-900 mb-2">Smart Control</div>
                  <div className="text-xs text-gray-500">Mobile App</div>
                </div>

                {/* Card 4 */}
                <div className="bg-white rounded-3xl p-6 shadow-2xl transform -rotate-1 hover:rotate-0 transition-all duration-500 border border-gray-100">
                  <div className="text-4xl mb-4">🤖</div>
                  <div className="text-sm font-bold text-gray-900 mb-2">SmartCode AI</div>
                  <div className="text-xs text-gray-500">1000 prints/min</div>
                </div>
              </div>
            </div>

            {/* Geometric Shapes */}
            <div className="absolute top-20 right-20 w-20 h-20 bg-[#0071bc]/10 rounded-full blur-xl"></div>
            <div className="absolute bottom-40 left-20 w-32 h-32 bg-[#1e40af]/10 rounded-full blur-2xl"></div>
          </div>
        </div>
      </section>

      {/* Industries Strip */}
      <section className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Trusted Across Industries</h2>
            <p className="text-gray-600">Serving diverse food and beverage sectors worldwide</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {industries.map((industry, index) => (
              <div key={index} className="text-center group">
                <div 
                  className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-all duration-300 shadow-lg"
                  style={{ backgroundColor: `${industry.color}20` }}
                >
                  <industry.icon className="w-10 h-10" style={{ color: industry.color }} />
                </div>
                <h3 className="font-bold text-gray-900 mb-1">{industry.name}</h3>
                <p className="text-sm text-gray-500">{industry.count} customers</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Carousel */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-8">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-6">Choose Your Solution</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Three powerful printing systems designed for different scales and requirements
            </p>
          </div>

          {/* Printer Selector */}
          <div className="flex justify-center mb-16">
            <div className="flex bg-gray-100 rounded-2xl p-2">
              {printers.map((printer, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedPrinter(index)}
                  className={`px-8 py-4 rounded-xl font-bold transition-all duration-300 ${
                    selectedPrinter === index
                      ? 'bg-white text-gray-900 shadow-lg'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {printer.name}
                </button>
              ))}
            </div>
          </div>

          {/* Selected Printer Display */}
          <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl overflow-hidden shadow-2xl border border-gray-100">
            <div className="grid lg:grid-cols-3 gap-0">
              {/* Product Info */}
              <div className="lg:col-span-2 p-12">
                <div className="space-y-8">
                  {/* Header */}
                  <div className="space-y-4">
                    <div 
                      className="inline-block px-4 py-2 rounded-full text-white font-bold text-sm"
                      style={{ backgroundColor: printers[selectedPrinter].color }}
                    >
                      {printers[selectedPrinter].type}
                    </div>
                    <h3 className="text-4xl font-black text-gray-900">{printers[selectedPrinter].name}</h3>
                    <div className="flex items-center gap-4">
                      <span className="text-2xl font-bold" style={{ color: printers[selectedPrinter].color }}>
                        {printers[selectedPrinter].price}
                      </span>
                      <span className="text-gray-500">{printers[selectedPrinter].model}</span>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-lg text-gray-600 leading-relaxed">
                    {printers[selectedPrinter].description}
                  </p>

                  {/* Features Grid */}
                  <div className="grid md:grid-cols-2 gap-6">
                    {printers[selectedPrinter].features.map((feature, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <div 
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: printers[selectedPrinter].color }}
                        ></div>
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* Stats */}
                  <div className="flex items-center gap-8 pt-4">
                    {printers[selectedPrinter].stats.map((stat, index) => (
                      <div key={index} className="text-center">
                        <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
                        <div className="text-sm text-gray-500">{stat.label}</div>
                      </div>
                    ))}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-4 pt-4">
                    <button 
                      className="px-8 py-3 rounded-xl text-white font-bold hover:scale-105 transition-all duration-300 shadow-lg"
                      style={{ backgroundColor: printers[selectedPrinter].color }}
                    >
                      GET QUOTE
                    </button>
                    <button className="px-8 py-3 border-2 border-gray-300 rounded-xl font-bold text-gray-700 hover:border-gray-400 transition-all duration-300">
                      LEARN MORE
                    </button>
                  </div>
                </div>
              </div>

              {/* Visual Section */}
              <div 
                className="relative flex items-center justify-center p-12"
                style={{ backgroundColor: printers[selectedPrinter].lightColor }}
              >
                {/* Product Mockup */}
                <div className="relative">
                  <div className="w-64 h-80 bg-white rounded-3xl shadow-2xl border border-gray-200 p-8 flex flex-col">
                    {/* Screen */}
                    <div 
                      className="h-32 rounded-2xl mb-6 flex items-center justify-center"
                      style={{ backgroundColor: printers[selectedPrinter].color }}
                    >
                      <div className="text-6xl text-white">{printers[selectedPrinter].image}</div>
                    </div>
                    
                    {/* Details */}
                    <div className="flex-1 space-y-4">
                      <div className="text-center">
                        <div className="font-bold text-gray-900 mb-2">{printers[selectedPrinter].model}</div>
                        <div className="flex items-center justify-center gap-2">
                          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                          <span className="text-sm text-gray-500">Ready</span>
                        </div>
                      </div>
                      
                      {/* Sample Output */}
                      <div className="bg-gray-100 rounded-xl p-4 text-center">
                        <div className="text-xs text-gray-500 mb-2">Sample Output</div>
                        <div className="font-mono text-sm font-bold text-gray-800">
                          EXP: {new Date(Date.now() + 30*24*60*60*1000).toLocaleDateString()}
                        </div>
                        <div className="font-mono text-xs text-gray-600 mt-1">
                          LOT: {Math.floor(Math.random() * 10000)}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Floating Elements */}
                  <div className="absolute -top-4 -right-4 w-8 h-8 bg-white rounded-full shadow-lg animate-pulse"></div>
                  <div className="absolute -bottom-4 -left-4 w-6 h-6 bg-white rounded-full shadow-lg animate-pulse delay-1000"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Showcase */}
      <section className="py-24 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-8">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-6">Why Industry Leaders Choose Us</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Advanced technology meets reliability in our comprehensive food printing solutions
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-12">
            {[
              {
                icon: Shield,
                title: 'FDA Compliance',
                description: 'All materials and inks meet stringent FDA food safety standards',
                stats: '100% Certified'
              },
              {
                icon: Monitor,
                title: 'Real-Time Monitoring',
                description: 'Advanced analytics and remote monitoring capabilities',
                stats: '24/7 Tracking'
              },
              {
                icon: Smartphone,
                title: 'Smart Integration',
                description: 'Seamless connection with existing production systems',
                stats: 'API Ready'
              }
            ].map((feature, index) => (
              <div key={index} className="text-center group">
                <div className="w-20 h-20 bg-[#0071bc] rounded-2xl flex items-center justify-center mx-auto mb-8 group-hover:scale-110 transition-all duration-300">
                  <feature.icon className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-4">{feature.title}</h3>
                <p className="text-gray-300 leading-relaxed mb-4">{feature.description}</p>
                <div className="text-[#0071bc] font-bold">{feature.stats}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24" style={{ background: 'linear-gradient(135deg, #0071bc 0%, #1e40af 100%)' }}>
        <div className="max-w-4xl mx-auto px-8 text-center text-white">
          <h2 className="text-5xl font-bold mb-8">Ready to Transform Your Production?</h2>
          <p className="text-xl mb-12 opacity-90 max-w-2xl mx-auto">
            Join hundreds of food manufacturers who rely on our printing solutions for critical date coding and traceability.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <button className="bg-white text-[#0071bc] hover:bg-gray-100 px-12 py-4 rounded-2xl font-bold text-lg transition-all duration-300 hover:scale-105 shadow-xl">
              START FREE TRIAL
            </button>
            <button className="border-2 border-white hover:bg-white hover:text-[#0071bc] px-12 py-4 rounded-2xl font-bold text-lg transition-all duration-300">
              SCHEDULE DEMO
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LaserPrinterWebsite;